import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from "@ngx-translate/core";
import {SharedSolutionActions} from "@shared/components/solution/store/shared-solution.actions-type";
import {SharedSolutionApiService} from "@shared/components/solution/services/shared-solution-api.service";
import {exhaustMap, withLatestFrom} from "rxjs";
import {SharedSolutionSelectors} from "@shared/components/solution/store/shared-solution.selector-type";
import {map} from "rxjs/operators";
import {ILocalPagination} from "@shared/interfaces/pagination.interface";
import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";
import {SolutionItemDto} from "@server-models";

@Injectable({
  providedIn: 'root',
})
export class SharedSolutionEffects {
  private _store = inject(Store);
  private _actions$ = inject(Actions);
  private _solutionApiService = inject(SharedSolutionApiService);


  loadSolution$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedSolutionActions.loadSolution),
      exhaustMap(({ missionId, filters, noteTopic }) => {
          return this._solutionApiService
            .loadSolution(missionId, { pageNumber: 1, pageSize: 50 } as ILocalPagination, filters, noteTopic)
            .pipe(
              map(output => SharedSolutionActions.loadSolutionSuccess({
                list: {
                  paging: output.paging,
                  filters: filters ?? [],
                  items: output.items
                },
                noteTopic
              }))
            );
        }
      )
    )
  );

  appendSolutionPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedSolutionActions.appendSolutionPage),
      withLatestFrom(
        this._store.select(SharedSolutionSelectors.selectMissionId),
        this._store.select(SharedSolutionSelectors.selectSolutionList),
        this._store.select(SharedSolutionSelectors.selectNoteTopic)
      ),
      exhaustMap(([_, missionId, currentList, noteTopic]) => {
          return this._solutionApiService
            .loadSolution(
              missionId ?? 0,
              {
                ...currentList.paging,
                pageNumber: ++currentList.paging.pageNumber
              },
              currentList.filters ?? [],
              noteTopic
            ).pipe(
              map(output => SharedSolutionActions.loadSolutionSuccess({
                list: {
                  paging: output.paging,
                  filters: currentList.filters ?? [],
                  items: [...(currentList?.items ?? []), ...output.items]
                },
                noteTopic
              }))
            );
        }
      )
    )
  );

  postSolutionItem$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedSolutionActions.postSolutionItem),
      withLatestFrom(
        this._store.select(SharedSolutionSelectors.selectMissionId),
        this._store.select(SharedSolutionSelectors.selectSolutionList),
        this._store.select(SharedSolutionSelectors.selectNoteTopic)
      ),
      exhaustMap(([{ note, solutionId }, missionId, { filters }, noteTopic]) => {
          const solutionItem: SolutionItemDto = {
            note,
            solutionId,
            missionId: missionId ?? 0,
            tenantId: note.tenantId
          }
          return this._solutionApiService.postSolutionItem(solutionItem).pipe(
            map(output => SharedSolutionActions.loadSolution({
                missionId: missionId ?? 0,
                filters,
                noteTopic
              })
            )
          )
        }
      )
    )
  );
}
